import { LANGUAGE_DEFAULT, NError, NSuccess } from '@configs/const.config'
import { TResApi, TResApiErr, TResDataListApi } from '@configs/interface.config'
import { checkAuth } from '@src/libs/localStorage'
import { TPatchPost, TPost, TPostViewSeriesResponse, TQueryPost, TQueryPostViewSeries } from '@src/modules'
import { notification } from 'antd'
import { useInfiniteQuery, useMutation, useQuery } from 'react-query'

import { queryClient } from '..'
import {
  createPost,
  getListPost,
  getListPostApp,
  getPostById,
  getPostViewSeries,
  getTotalPost,
  patchPostById,
  putSlugPostById,
  removeManyPostByIds,
  removePostById,
} from '../apis'
import { DETAIL_POST, LIST_POST, LIST_POST_INFINITE, POST_VIEW_SERIES, TOTAL_POST } from '../keys'

/**
 *
 * @method useMutationCreatePost
 * @returns
 */
export const useMutationCreatePost = () =>
  useMutation(createPost, {
    onSuccess: (res: TResApi<TPost>) => {
      queryClient.refetchQueries([LIST_POST]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 * @method useQueryListPost
 * @param {TQueryPost}params
 * @param {string}token
 * @param lang
 * @returns
 */
export const useQueryListPost = (params: TQueryPost, token?: string, lang = LANGUAGE_DEFAULT) => {
  const accessToken = token || checkAuth()
  return useQuery<TResDataListApi<TPost[]>>(
    [LIST_POST, JSON.stringify(params), lang],
    () => getListPost(params, accessToken, lang),
    { enabled: !!accessToken },
  )
}

/**
 * @method useQueryPostTotal
 * @param {string}token
 * @returns
 */
export const useQueryPostTotal = (token?: string) => {
  const accessToken = token || checkAuth()
  return useQuery<TResApi<number>>([TOTAL_POST], () => getTotalPost(token), { enabled: !!accessToken })
}

/**
 * @method useQueryPostById
 * @param {string}id
 * @param {string}token
 * @param lang
 * @returns
 */
export const useQueryPostById = (id: string, token?: string, lang = LANGUAGE_DEFAULT) => {
  const accessToken = token || checkAuth()
  return useQuery<TResApi<TPost>>([DETAIL_POST, id, lang], () => getPostById(id, token, lang), {
    enabled: !!accessToken && !!id,
  })
}

export const useQueryPostViewSeries = (id: string, params: TQueryPostViewSeries, token?: string) => {
  const accessToken = token || checkAuth()
  return useQuery<TResApi<TPostViewSeriesResponse>>(
    [POST_VIEW_SERIES, id, params.from, params.to],
    () => getPostViewSeries(id, params, accessToken),
    { enabled: !!accessToken && !!id && !!params.from && !!params.to },
  )
}

/**
 *
 * @method useMutationPatchPostById
 * @returns
 */
export const useMutationPatchPostById = () =>
  useMutation(({ id, data }: { id: string; data: TPatchPost }) => patchPostById(id, data), {
    onSuccess: (res: TResApi, { id }) => {
      queryClient.refetchQueries([LIST_POST]).then()
      queryClient.invalidateQueries([DETAIL_POST, id]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 *
 * @method useMutationRemovePostById
 * @returns
 */
export const useMutationRemovePostById = () =>
  useMutation((id: string) => removePostById(id), {
    onSuccess: (res: TResApi, id) => {
      queryClient.refetchQueries([LIST_POST]).then()
      queryClient.removeQueries([DETAIL_POST, id])
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 *
 * @method useMutationRemoveManyPostByIds
 * @returns
 */
export const useMutationRemoveManyPostByIds = () =>
  useMutation(removeManyPostByIds, {
    onSuccess: (res: TResApi) => {
      // [TODO] ...
      queryClient.invalidateQueries([LIST_POST]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 *
 * @method useMutationPutSlugPostById
 * @returns
 */
export const useMutationPutSlugPostById = () =>
  useMutation(({ id, slug }: { id: string; slug: string }) => putSlugPostById(id, slug), {
    onSuccess: (res: TResApi, { id, slug }) => {
      queryClient.setQueryData([DETAIL_POST, id], () => {
        const old = queryClient.getQueryData<TResApi<TPost>>([DETAIL_POST, id])
        if (old && old?.data)
          return {
            ...old,
            data: { ...old.data, slug },
          }
        return old
      })
      // queryClient.refetchQueries([LIST_POST])
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 * useInfinteQueryListPost
 * @param params
 * @param token
 * @returns
 */
export const useInfinteQueryListPost = (params: TQueryPost, token?: string) => {
  const accessToken = token || checkAuth()

  return useInfiniteQuery<TResDataListApi<TPost[]>>(
    [LIST_POST_INFINITE, JSON.stringify(params)],
    ({ pageParam = 1 }) => getListPostApp({ ...params, page: pageParam }, accessToken),
    {
      refetchOnWindowFocus: true,
      retry: 2,
      enabled: !!accessToken,
      getNextPageParam: (lastPage) =>
        lastPage?.page < Math.ceil(lastPage.total / lastPage.limit) ? lastPage.page + 1 : undefined,
    },
  )
}
